import React, { type ReactElement, Suspense, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom'
import { Box, ThemeProvider } from '@mui/material'
import { useStatsigClient, useStatsigUser } from '@statsig/react-bindings'

import { apiZodios } from '~api/apiZodios'
import outlookZodios from '~api/outlookApiZodios'
import { CentralEnvironmentIndicator } from '~components/common/EnvironmentIndicator'
import { IS_PROD } from '~config'
import { FEATURE_FLAGS } from '~featureFlags'
import { useFeatureGate } from '~hooks/access/useFeatureGate'
import useAmplitudeIdentification from '~hooks/useAmplitudeIdentification'
import { orgHandlesMatch, useUrlOrganizationHandle } from '~hooks/useUrlOrganizationHandle'
import AIInteractionLogViewerPage from '~pages/AISettingsScreen/pages/AIInteractionLogViewerPage'
import AIPageSourceViewerPage from '~pages/AISettingsScreen/pages/AIPageSourceViewerPage'
import TopHeader from '~pages/ConstituentScreens/components/TopHeader'
import LegacySettingsRedirect from '~pages/OrganizationSettings/SettingsHubScreen/LegacySettingsRedirect'
import { setIsOutlookMode, setUser } from '~redux/slices/authSlice'
import { setEmailIdentities, setSelectedEIOrganization } from '~redux/slices/organizationSlice'
import { SkinEnum, type UserId } from '~types'
import { fetchPermissions } from '~utils/permissionsUtils'

import { SessionTimeoutManager } from './components/common/SessionTimeout'
import BannerDisplay from './components/internal/BannerDisplay'
import { BANNERS } from './components/layout/bannerConfig'
import Sidebar from './components/layout/Sidebar'
import {
  OrganizationScopedPage,
  TopBar,
  WorkspaceChangeRedirect,
  WorkspaceRedirectProvider,
} from './components/layout/TopBar'
import './components/styles/global.less'
import { internalLightTheme } from './createInternalTheme'
import { type RootState } from './redux/rootReducer'
import { type AppDispatch, persistor } from './redux/store'
import RequireAuth from './RequireAuth'
import RequireFeatureFlag from './RequireFeatureFlag'
import RequireProfessionalAuth from './RequireProfessionalAuth'
import RequireStaffMemberAuth from './RequireStaffMemberAuth'

const AccountSettingsScreen = React.lazy(
  () => import('./pages/AccountSettingsScreen/AccountSettingsScreen'),
)
const AIAnalyticsPage = React.lazy(() => import('./pages/AISettingsScreen/pages/AIAnalyticsPage'))
const AIAssistantScreen = React.lazy(() => import('./pages/AIAssistantScreen/AIAssistantScreen'))
const AISettingsScreen = React.lazy(() => import('./pages/AISettingsScreen/AISettingsScreen'))
const HealthcheckScreen = React.lazy(() => import('./pages/HealthcheckScreen/HealthcheckScreen'))
const HomepageScreen = React.lazy(() => import('./pages/HomepageScreen'))
const AICustomizePage = React.lazy(() => import('./pages/AISettingsScreen/pages/AICustomizePage'))
const AIInteractionLogTablePage = React.lazy(
  () => import('./pages/AISettingsScreen/pages/AIInteractionLogTablePage'),
)
const KnowledgeBaseScreen = React.lazy(
  () => import('./pages/KnowledgeBaseScreen/pages/KnowledgeBaseScreen'),
)
const LinksTab = React.lazy(() => import('./pages/KnowledgeBaseScreen/pages/LinksTab'))
const DomainsTab = React.lazy(() => import('./pages/KnowledgeBaseScreen/pages/DomainsTab'))
const DocumentsTab = React.lazy(() => import('./pages/KnowledgeBaseScreen/pages/DocumentsTab'))
const FAQsTab = React.lazy(() => import('./pages/KnowledgeBaseScreen/pages/FAQsTab'))
const ProcessesTab = React.lazy(() => import('./pages/KnowledgeBaseScreen/pages/ProcessesTab'))
const TransferLinesTab = React.lazy(
  () => import('./pages/KnowledgeBaseScreen/pages/TransferLinesTab/TransferLinesTab'),
)
const GISLayersTab = React.lazy(() => import('./pages/KnowledgeBaseScreen/pages/GISLayersTab'))
const IntegrationsTab = React.lazy(
  () => import('./pages/KnowledgeBaseScreen/pages/IntegrationsTab'),
)
const AnalyticsScreen = React.lazy(() => import('./pages/AnalyticsScreen/AnalyticsScreen'))
const CasesScreen = React.lazy(() => import('./pages/CasesScreen/CasesScreen'))
const CaseFeedRouter = React.lazy(() => import('./pages/CasesScreen/CaseFeedRouter'))
const CreateCaseScreen = React.lazy(() => import('./pages/CasesScreen/CreateCaseScreen/'))
const CaseApplyPage = React.lazy(() => import('./pages/ConstituentScreens/CaseApplyPage'))
const ConstituentCasesPage = React.lazy(
  () => import('./pages/ConstituentScreens/ConstituentCasesPage'),
)
const ConstituentLicensesPage = React.lazy(
  () => import('./pages/ConstituentScreens/ConstituentLicensesPage'),
)
const ConstituentPortalPage = React.lazy(
  () => import('./pages/ConstituentScreens/ConstituentPortalPage'),
)
const ContactPage = React.lazy(() => import('./pages/ContactsScreen/ContactPage'))
const ContactPageV3 = React.lazy(() => import('./pages/ContactsScreen/ContactPage/cmv3'))
const ContactsScreenV3 = React.lazy(() => import('./pages/ContactsScreen/ContactsScreen/cmv3'))
const MassCommunicationsListPage = React.lazy(
  () =>
    import('./pages/MassCommunicationsScreen/pages/MassCommunicationsScreen/MassCommunicationsListPage'),
)
const MassCommunicationEditorPage = React.lazy(
  () => import('./pages/MassCommunicationsScreen/pages/MassCommunicationsScreen'),
)
const MassCommunicationAnalyticsPage = React.lazy(
  () =>
    import('./pages/MassCommunicationsScreen/pages/MassCommunicationsScreen/MassCommunicationAnalyticsPage'),
)
const MenuScreen = React.lazy(() => import('./pages/MenuScreen/pages/Menu'))
const FormLetterPage = React.lazy(() => import('./pages/OrganizationSettings/pages/FormLetterPage'))
const LicenseTemplatesScreen = React.lazy(() => import('./pages/LicensesScreen'))
const LicensePage = React.lazy(() => import('./pages/LicensesScreen/pages/LicensePage'))
const LicenseEditorPage = React.lazy(() => import('./pages/LicensesScreen/pages/LicenseEditorPage'))
const LicenseTemplatePage = React.lazy(
  () => import('./pages/LicensesScreen/pages/LicenseTemplatePage'),
)
const OrganizationInviteScreen = React.lazy(
  () => import('./pages/OrganizationSettings/OrganizationInviteScreen'),
)
const WelcomeScreen = React.lazy(() => import('./pages/WelcomeScreen/WelcomeScreen'))
const SettingsHubScreen = React.lazy(() => import('./pages/OrganizationSettings/SettingsHubScreen'))
const OutlookScreen = React.lazy(() => import('./pages/OutlookScreen/OutlookScreen'))
const PaymentBillPage = React.lazy(() => import('./pages/PaymentsScreen/pages/PaymentBillPage'))
const PaymentLookupPage = React.lazy(
  () =>
    import('./pages/PaymentsScreen/pages/PaymentLookupPage/pages/PaymentLookupPage/PaymentLookupPage'),
)
const PaymentLookupPayPage = React.lazy(
  () => import('./pages/PaymentsScreen/pages/PaymentLookupPayPage'),
)
const ReceiptPage = React.lazy(() => import('./pages/PaymentsScreen/pages/ReceiptPage'))
const TakePaymentScreen = React.lazy(
  () => import('./pages/PaymentsScreen/pages/TakePaymentScreen/TakePaymentScreen'),
)
const PaymentsScreen = React.lazy(
  () => import('./pages/PaymentsScreen/pages/PaymentsScreen/PaymentsScreen'),
)
const ProcessFlowEditPage = React.lazy(
  () => import('./pages/ProcessesScreen/pages/ProcessFlowEditPage'),
)
const ProcessPage = React.lazy(() => import('./pages/ProcessesScreen/pages/ProcessPage'))
const ProcessApplyPage = React.lazy(() => import('./pages/ProcessesScreen/ProccessApplyScreen'))
const ProcessesScreen = React.lazy(() => import('./pages/ProcessesScreen/ProcessesScreen'))
const CreateWorkflowScreen = React.lazy(
  () => import('./pages/CreateWorkflowScreen/pages/CreateWorkflowScreen'),
)
const CreateProcessRedirect = (): ReactElement => {
  const { search } = useLocation()
  return <Navigate to={{ pathname: '/create-workflow', search }} replace />
}
const ResetPasswordScreen = React.lazy(
  () => import('./pages/ResetPasswordScreen/ResetPasswordScreen'),
)
const SuperUserDashboard = React.lazy(() => import('./pages/SuperScreen/SuperScreen'))
const ChatEmbedScreen = React.lazy(
  () => import('./pages/AISettingsScreen/components/ChatEmbedScreen'),
)
const SearchEmbedScreen = React.lazy(
  () => import('./pages/AISettingsScreen/components/SearchEmbedScreen'),
)
const ChangelogScreen = React.lazy(() => import('./pages/ChangelogScreen/ChangelogScreen'))
const Root = React.lazy(() => import('./pages/Root'))
const VerifyScreen = React.lazy(() => import('./pages/VerifyScreen'))
const SSOConfirmLinkScreen = React.lazy(() => import('./pages/SSOConfirmLinkScreen'))
const DesignSystemInternal = React.lazy(() => import('./pages/DesignSystem/DesignSystemInternal'))
const PageNotFoundScreen = React.lazy(() => import('./pages/PageNotFoundScreen'))
const PublicRecordsStudioRedactionScreen = React.lazy(
  () => import('~pages/PublicRecordsStudioRedactionScreen/PublicRecordsStudioRedactionScreen'),
)
const PublicRecordsStudioRequestsScreen = React.lazy(
  () => import('~pages/PublicRecordsStudioRequestsScreen/PublicRecordsStudioRequestsScreen'),
)

const HealthCheck = (): ReactElement => {
  return <div>OK</div>
}

const NotFound = (): ReactElement => {
  return (
    <Suspense fallback={<EmbedLoading />}>
      <PageNotFoundScreen permissionsError={false} />
    </Suspense>
  )
}

const routeTitles: { [key: string]: string } = {
  '/': 'Home',
  '/cases': 'Cases',
  '/create-case': 'Create Case',
  '/contacts': 'Contacts',
  '/processes': 'Workflows',
  '/bills': 'Payments',
  '/collect-payment': 'Collect Payment',
  '/analytics': 'Analytics',
  '/changelog': "What's New",
  '/menu': 'Menu',
  '/my-cases': 'My Cases',
  '/my-licenses-and-permits': 'My Licenses and Permits',
  '/account': 'Account Settings',
  '/mass-communication': 'Outbound Communications',
  '/form-letters': 'Form Letters',
  '/licenses-and-permits': 'Licenses and Permits',
  '/public-records': 'Public Records',
  '/home': 'Home',
  '/ai-log': 'AI Log',
  '/settings': 'Settings',
  '/settings-hub': 'Settings',
  '/assistant': 'Assistant',
  '/verify': 'Verify',
  '/reset-password': 'Reset Password',
  '/design-system-internal': 'Design System Internal',
}

const orgRouteTitles: { [key: string]: string } = {
  sources: 'Content',
  'sources/links': 'Links',
  'sources/domains': 'Domains',
  'sources/documents': 'Documents',
  'sources/faqs': 'FAQs',
  'sources/processes': 'Workflows',
  'sources/transfer-lines': 'Transfer Lines',
  'sources/gis-layers': 'GIS Layers',
  'sources/integrations': 'Integrations',
  analytics: 'Analytics',
  'health-check': 'Health Check',
  'ai-customize': 'Customize',
  'ai-log': 'AI Log',
  settings: 'Settings',
  chat: 'Chat',
  search: 'Search',
  query: 'Search',
  blueprints: 'Workflows',
  workflows: 'Workflows',
}

const TitleUpdater = (): null => {
  const location = useLocation()
  const orgName = useSelector((state: RootState) => state?.organization.selectedOrganization?.name)

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean)
    const isOrgRoute = pathParts[0] === 'organizations' && pathParts.length > 1

    let title: string
    if (isOrgRoute) {
      const subPath = pathParts[2] || ''
      const twoSegmentSubPath = pathParts[3] ? `${subPath}/${pathParts[3]}` : ''
      title =
        (twoSegmentSubPath && orgRouteTitles[twoSegmentSubPath]) ||
        orgRouteTitles[subPath] ||
        'AI Front Desk'
    } else {
      const baseRoute = '/' + (pathParts[0] || '')
      title = routeTitles[baseRoute] || routeTitles[location.pathname] || 'Polimorphic'
    }

    const parts = [title, ...(isOrgRoute ? [orgName] : []), 'Polimorphic'].filter(Boolean)
    document.title = parts.join(' | ')
  }, [location, orgName])

  return null
}

const EmbedLoading = (): ReactElement => (
  <div className='flex items-center justify-center w-full h-full'>
    <div className='w-8 h-8 border-4 border-blue-500 rounded-full animate-spin border-t-transparent'></div>
  </div>
)

const INTERNAL_KNOWLEDGE_BASE_ROUTES = [
  'links',
  'documents',
  'domains',
  'processes',
  'gis-layers',
  'faqs',
  'transfer-lines',
  'integrations',
]

const UNIFIED_KNOWLEDGE_BASE_ROUTES = ['sources', 'links', 'documents', 'domains', 'faqs']

interface KnowledgeBaseRouteForOrganizationProps {
  organizationHandle?: string
}

const KnowledgeBaseRouteForOrganization = ({
  organizationHandle,
}: KnowledgeBaseRouteForOrganizationProps): ReactElement => {
  const location = useLocation()
  const { enabled, loading } = useFeatureGate(FEATURE_FLAGS.KNOWLEDGE_BASE_REDESIGN, {
    organizationHandle,
  })
  const route = location.pathname.split('/').filter(Boolean).at(-1) ?? 'sources'
  const usesInternalTheme =
    loading ||
    INTERNAL_KNOWLEDGE_BASE_ROUTES.includes(route) ||
    UNIFIED_KNOWLEDGE_BASE_ROUTES.includes(route)
  const content = (
    <Suspense fallback={<EmbedLoading />}>
      <KnowledgeBaseScreen redesignEnabled={enabled} redesignLoading={loading} />
    </Suspense>
  )

  return usesInternalTheme ? (
    <ThemeProvider theme={internalLightTheme}>{content}</ThemeProvider>
  ) : (
    content
  )
}

const KnowledgeBaseRoute = (): ReactElement => {
  const { organizationHandle } = useParams<{ organizationHandle: string }>()
  return (
    <KnowledgeBaseRouteForOrganization
      key={organizationHandle ?? 'no-organization'}
      organizationHandle={organizationHandle}
    />
  )
}

const ProcessEditRoute = (): ReactElement => {
  const { organizationHandle } = useParams()

  return (
    <Suspense fallback={<EmbedLoading />}>
      <RequireStaffMemberAuth organizationHandle={organizationHandle}>
        <ProcessFlowEditPage />
      </RequireStaffMemberAuth>
    </Suspense>
  )
}

const ProcessFlowEditRoute = (): ReactElement => {
  const { organizationHandle } = useParams()

  return (
    <Suspense fallback={<EmbedLoading />}>
      <RequireStaffMemberAuth organizationHandle={organizationHandle}>
        <ProcessFlowEditPage />
      </RequireStaffMemberAuth>
    </Suspense>
  )
}

// Layout route that mounts a single, persistent Sidebar above the child route
// boundary so the Sidebar instance survives navigations between sibling routes
// (no remount, no flash, no state loss). On narrow viewports (e.g. high zoom),
// the sidebar renders in its collapsed state so navigation remains accessible.
const SidebarLayout = (): ReactElement => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const user = useSelector((state: RootState) => state?.auth.user)
  const isLoggedIn = Boolean(userId) && user != null
  const showChrome = isLoggedIn && user.skin !== SkinEnum.enum.Casual

  if (!showChrome) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <SessionTimeoutManager />
        {isLoggedIn && <TopHeader />}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <SessionTimeoutManager />
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Sidebar />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ThemeProvider theme={internalLightTheme}>
            <BannerDisplay banners={BANNERS} />
          </ThemeProvider>
          <TopBar />
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const App = (): ReactElement => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const orgRefreshTrigger = useSelector((state: RootState) => state?.organization.refreshTrigger)
  const selectedOrganizationHandle = useSelector(
    (state: RootState) => state?.organization.selectedOrganization?.handle,
  )
  const urlOrganizationHandle = useUrlOrganizationHandle()
  const [appInitialized, setAppInitialized] = useState(false)
  const dispatch: AppDispatch = useDispatch()
  const isOutlookMode = useSelector((state: RootState) => state?.auth.isOutlookMode)

  useEffect(() => {
    const detectOutlook = (): void => {
      const isOutlook = window.Office !== undefined
      dispatch(setIsOutlookMode(isOutlook))
    }

    detectOutlook()
  }, [dispatch])

  const getUser = async (): Promise<void> => {
    if (!userId) {
      return
    }
    const isOutlook = typeof window !== 'undefined' && window.Office !== undefined
    const getUserFn = isOutlook ? outlookZodios.getUser : apiZodios.core_getUser
    const user = await getUserFn({ user_id: userId })
    dispatch(setUser(user))
  }

  const getEmailIdentities = async (): Promise<void> => {
    if (!userId) {
      return
    }
    try {
      const isOutlook = typeof window !== 'undefined' && window.Office !== undefined
      const listIdentitiesFn = isOutlook
        ? outlookZodios.listDistinctEmailIdentities
        : apiZodios.core_listDistinctEmailIdentities
      const response = await listIdentitiesFn({
        user_id: userId,
        u_first: false,
      })
      const identities = response.data
      dispatch(setEmailIdentities(identities))
      if (identities?.length > 0) {
        const matchingIdentity =
          (urlOrganizationHandle
            ? identities.find((i) => orgHandlesMatch(i.handle, urlOrganizationHandle))
            : undefined) ?? identities.find((i) => i.handle === selectedOrganizationHandle)
        const fallbackIdentity = identities.find((i) => i.organization_id != null) ?? identities[0]
        dispatch(setSelectedEIOrganization(matchingIdentity ?? fallbackIdentity))
      }
    } catch (error: unknown) {
      console.error('Error fetching email identities', error)
    }
  }

  useEffect(() => {
    const bootstrapped = persistor.getState().bootstrapped
    if (bootstrapped) {
      setAppInitialized(true)
      return
    }
    const unsubscribe = persistor.subscribe(() => {
      const { bootstrapped } = persistor.getState()
      if (bootstrapped) {
        setAppInitialized(true)
        unsubscribe()
      }
    })
    return (): void => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    getEmailIdentities()
    getUser()
  }, [userId])

  const { client: statsigClient } = useStatsigClient()
  const { updateUserAsync } = useStatsigUser()
  useEffect(() => {
    if (!userId) return
    const updateStatsigOrganizations = async (): Promise<void> => {
      const allPermissions = await fetchPermissions(userId as UserId)
      const organizationsList = [...new Set(Object.values(allPermissions).flat())]
      const existingCustom = statsigClient.getContext().user?.custom ?? {}
      await updateUserAsync({
        userID: userId,
        custom: { ...existingCustom, organizationsList },
      })
    }
    updateStatsigOrganizations().catch((err) => {
      console.error('Error updating Statsig organizations list', err)
    })
  }, [userId, updateUserAsync, statsigClient])

  useAmplitudeIdentification()

  useEffect(() => {
    if (appInitialized) {
      getEmailIdentities()
    }
  }, [orgRefreshTrigger])

  if (!appInitialized) {
    return <div key={'root-routes-key'} />
  }

  if (isOutlookMode) {
    return (
      <Box>
        <Routes>
          <Route path='/outlook-add-in' element={<OutlookScreen />} />
          <Route path='/' element={<Root />} />
        </Routes>
      </Box>
    )
  }

  return (
    <Box>
      <CentralEnvironmentIndicator />
      <Routes>
        {/* Public / embed / constituent routes — no sidebar */}
        <Route
          path='/'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <Root />
            </Suspense>
          }
        />
        <Route
          path='/payments/:organizationHandle/bills/:billCode'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <RequireAuth>
                <PaymentBillPage />
              </RequireAuth>
            </Suspense>
          }
        />
        <Route
          path='/payments/:organizationHandle/pay/:lookupKey'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <PaymentLookupPayPage />
            </Suspense>
          }
        />
        <Route
          path='/my-cases'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <RequireAuth>
                <ConstituentCasesPage />
              </RequireAuth>
            </Suspense>
          }
        />
        <Route
          path='/my-licenses-and-permits'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <RequireAuth>
                <RequireFeatureFlag flag={FEATURE_FLAGS.LICENSES_AND_PERMITS}>
                  <ConstituentLicensesPage />
                </RequireFeatureFlag>
              </RequireAuth>
            </Suspense>
          }
        />
        <Route
          path='/organizations/:organizationHandle/portal'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <ConstituentPortalPage />
            </Suspense>
          }
        />
        {selectedOrganizationHandle && (
          <Route
            path='/'
            element={
              <Navigate to={`/organizations/${selectedOrganizationHandle}/sources`} replace />
            }
          />
        )}
        <Route
          path='/organizations/:organizationHandle/workflows/:processCode/apply'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <ProcessApplyPage />
            </Suspense>
          }
        />
        <Route
          path='/organizations/:organizationHandle/blueprints/:processCode/apply'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <ProcessApplyPage />
            </Suspense>
          }
        />
        <Route
          path='/organizations/:organizationHandle/bills/:billCode/payments/:paymentCode/receipt'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <ReceiptPage />
            </Suspense>
          }
        />
        <Route
          path='/organizations/:organizationHandle/chat'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <ChatEmbedScreen />
            </Suspense>
          }
        />
        <Route
          path='/organizations/:organizationHandle/search'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <SearchEmbedScreen />
            </Suspense>
          }
        />
        {/* NOTE: Supporting legacy "query" route for existing embed codes */}
        <Route
          path='/organizations/:organizationHandle/query'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <SearchEmbedScreen />
            </Suspense>
          }
        />
        <Route
          path='/outlook-add-in'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <OutlookScreen />
            </Suspense>
          }
        />
        <Route
          path='/cases/:caseHandle/apply'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <RequireAuth>
                <CaseApplyPage />
              </RequireAuth>
            </Suspense>
          }
        />

        <Route
          path='/organizations/:organizationHandle/invite'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <RequireProfessionalAuth>
                <ThemeProvider theme={internalLightTheme}>
                  <OrganizationInviteScreen />
                </ThemeProvider>
              </RequireProfessionalAuth>
            </Suspense>
          }
        />

        {/* Sidebar persists across these via SidebarLayout */}
        <Route element={<SidebarLayout />}>
          <Route
            path='/cases'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <CasesScreen />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/create-case/*'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <CreateCaseScreen />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/cases/:caseHandle'
            element={
              <WorkspaceChangeRedirect to='/cases'>
                <Suspense fallback={<EmbedLoading />}>
                  <CaseFeedRouter />
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/contacts'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <ContactsScreenV3 />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/processes'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <ProcessesScreen />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route path='/create-process' element={<CreateProcessRedirect />} />
          <Route
            path='/create-workflow'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <CreateWorkflowScreen />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/bills'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <PaymentsScreen />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/collect-payment'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <TakePaymentScreen />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/payments/:organizationHandle/lookups/:paymentCode'
            element={
              <WorkspaceChangeRedirect to='/bills'>
                <Suspense fallback={<EmbedLoading />}>
                  <RequireProfessionalAuth>
                    <ThemeProvider theme={internalLightTheme}>
                      <PaymentLookupPage />
                    </ThemeProvider>
                  </RequireProfessionalAuth>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/analytics'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <AnalyticsScreen />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/assistant'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <AIAssistantScreen />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/changelog'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <ChangelogScreen />
              </Suspense>
            }
          />
          <Route
            path='/menu'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <MenuScreen />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/organizations/:organizationHandle/joined'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <WelcomeScreen />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/organizations/:organizationHandle'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <AISettingsScreen />
                </RequireProfessionalAuth>
              </Suspense>
            }
          >
            <Route
              path='analytics'
              element={
                <Suspense fallback={<EmbedLoading />}>
                  <ThemeProvider theme={internalLightTheme}>
                    <AIAnalyticsPage />
                  </ThemeProvider>
                </Suspense>
              }
            />
            <Route path='sources' element={<KnowledgeBaseRoute />}>
              <Route
                index
                element={
                  <Suspense fallback={<EmbedLoading />}>
                    <LinksTab />
                  </Suspense>
                }
              />
              <Route
                path='links'
                element={
                  <Suspense fallback={<EmbedLoading />}>
                    <LinksTab />
                  </Suspense>
                }
              />
              <Route
                path='domains'
                element={
                  <Suspense fallback={<EmbedLoading />}>
                    <DomainsTab />
                  </Suspense>
                }
              />
              <Route
                path='documents'
                element={
                  <Suspense fallback={<EmbedLoading />}>
                    <DocumentsTab />
                  </Suspense>
                }
              />
              <Route
                path='faqs'
                element={
                  <Suspense fallback={<EmbedLoading />}>
                    <FAQsTab />
                  </Suspense>
                }
              />
              <Route
                path='processes'
                element={
                  <Suspense fallback={<EmbedLoading />}>
                    <ProcessesTab />
                  </Suspense>
                }
              />
              <Route
                path='transfer-lines'
                element={
                  <Suspense fallback={<EmbedLoading />}>
                    <TransferLinesTab />
                  </Suspense>
                }
              />
              <Route
                path='gis-layers'
                element={
                  <Suspense fallback={<EmbedLoading />}>
                    <GISLayersTab />
                  </Suspense>
                }
              />
              <Route
                path='integrations'
                element={
                  <Suspense fallback={<EmbedLoading />}>
                    <IntegrationsTab />
                  </Suspense>
                }
              />
            </Route>
            <Route
              path='sources/:pageSourceId'
              element={
                <OrganizationScopedPage root='sources'>
                  <Suspense fallback={<EmbedLoading />}>
                    <ThemeProvider theme={internalLightTheme}>
                      <AIPageSourceViewerPage />
                    </ThemeProvider>
                  </Suspense>
                </OrganizationScopedPage>
              }
            />
            <Route path='test-lab' element={<Navigate to='../health-check' replace />} />
            <Route
              path='health-check'
              element={
                <Suspense fallback={<EmbedLoading />}>
                  <HealthcheckScreen />
                </Suspense>
              }
            />
            <Route
              path='health-check/questions'
              element={
                <Suspense fallback={<EmbedLoading />}>
                  <HealthcheckScreen />
                </Suspense>
              }
            />
            <Route
              path='health-check/:runId'
              element={
                <OrganizationScopedPage root='health-check'>
                  <Suspense fallback={<EmbedLoading />}>
                    <HealthcheckScreen />
                  </Suspense>
                </OrganizationScopedPage>
              }
            />
            <Route
              path='health-check/:runId/interaction/:scenarioId'
              element={
                <OrganizationScopedPage root='health-check'>
                  <Suspense fallback={<EmbedLoading />}>
                    <HealthcheckScreen />
                  </Suspense>
                </OrganizationScopedPage>
              }
            />
            <Route
              path='ai-customize'
              element={
                <Suspense fallback={<EmbedLoading />}>
                  <ThemeProvider theme={internalLightTheme}>
                    <AICustomizePage />
                  </ThemeProvider>
                </Suspense>
              }
            />
            <Route path='ai-publish' element={<LegacySettingsRedirect section='channels' />} />
            <Route
              path='ai-log'
              element={
                <OrganizationScopedPage root='ai-log'>
                  <Suspense fallback={<EmbedLoading />}>
                    <AIInteractionLogTablePage />
                  </Suspense>
                </OrganizationScopedPage>
              }
            >
              <Route path=':aiInteractionId/' element={null} />
              <Route path=':aiInteractionId/testing' element={null} />
            </Route>
            <Route index element={<Navigate to='sources' replace />} />
          </Route>
          <Route
            path='/ai-log/:aiInteractionId'
            element={
              <OrganizationScopedPage root='ai-log'>
                <Suspense fallback={<EmbedLoading />}>
                  <RequireProfessionalAuth>
                    <ThemeProvider theme={internalLightTheme}>
                      <AIInteractionLogViewerPage />
                    </ThemeProvider>
                  </RequireProfessionalAuth>
                </Suspense>
              </OrganizationScopedPage>
            }
          />
          <Route
            path='/settings-hub/:section?'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <SettingsHubScreen />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/organizations/:organizationHandle/settings/:settingType'
            element={<LegacySettingsRedirect />}
          />
          <Route path='/settings/:settingType' element={<LegacySettingsRedirect />} />
          <Route
            path='/organizations/:organizationHandle/contacts/:contactHandle'
            element={
              <WorkspaceChangeRedirect to='/contacts'>
                <Suspense fallback={<EmbedLoading />}>
                  <ThemeProvider theme={internalLightTheme}>
                    <ContactPage />
                  </ThemeProvider>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          >
            <Route path=':aiInteractionId/' element={null} />
          </Route>
          <Route
            path='/contacts/:contactBookCode/:contactHandle'
            element={
              <WorkspaceChangeRedirect to='/contacts'>
                <Suspense fallback={<EmbedLoading />}>
                  <ThemeProvider theme={internalLightTheme}>
                    <ContactPageV3 />
                  </ThemeProvider>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          >
            <Route path=':aiInteractionId/' element={null} />
          </Route>
          <Route
            path='/organizations/:organizationHandle/workflows/:processCode/history'
            element={
              <WorkspaceChangeRedirect to='/processes'>
                <Suspense fallback={<EmbedLoading />}>
                  <ThemeProvider theme={internalLightTheme}>
                    <ProcessPage />
                  </ThemeProvider>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/organizations/:organizationHandle/blueprints/:processCode/history'
            element={
              <WorkspaceChangeRedirect to='/processes'>
                <Suspense fallback={<EmbedLoading />}>
                  <ThemeProvider theme={internalLightTheme}>
                    <ProcessPage />
                  </ThemeProvider>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/organizations/:organizationHandle/workflows/:processCode/dashboard'
            element={
              <WorkspaceChangeRedirect to='/processes'>
                <Suspense fallback={<EmbedLoading />}>
                  <ThemeProvider theme={internalLightTheme}>
                    <ProcessPage />
                  </ThemeProvider>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/organizations/:organizationHandle/blueprints/:processCode/dashboard'
            element={
              <WorkspaceChangeRedirect to='/processes'>
                <Suspense fallback={<EmbedLoading />}>
                  <ThemeProvider theme={internalLightTheme}>
                    <ProcessPage />
                  </ThemeProvider>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/organizations/:organizationHandle/workflows/:processCode/edit'
            element={
              <WorkspaceChangeRedirect to='/processes'>
                <ProcessEditRoute />
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/organizations/:organizationHandle/blueprints/:processCode/edit'
            element={
              <WorkspaceChangeRedirect to='/processes'>
                <ProcessEditRoute />
              </WorkspaceChangeRedirect>
            }
          />
          {!IS_PROD && (
            <>
              <Route
                path='/organizations/:organizationHandle/workflows/:processCode/edit-flow'
                element={
                  <WorkspaceChangeRedirect to='/processes'>
                    <ProcessFlowEditRoute />
                  </WorkspaceChangeRedirect>
                }
              />
              <Route
                path='/organizations/:organizationHandle/blueprints/:processCode/edit-flow'
                element={
                  <WorkspaceChangeRedirect to='/processes'>
                    <ProcessFlowEditRoute />
                  </WorkspaceChangeRedirect>
                }
              />
            </>
          )}
          <Route
            path='/account/*'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <AccountSettingsScreen />
                  </ThemeProvider>
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path='/mass-communication'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <MassCommunicationsListPage />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/mass-communication/:orgHandle'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <MassCommunicationsListPage />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/mass-communication/:orgHandle/:id'
            element={
              <WorkspaceChangeRedirect to='/mass-communication'>
                <Suspense fallback={<EmbedLoading />}>
                  <RequireProfessionalAuth>
                    <ThemeProvider theme={internalLightTheme}>
                      <MassCommunicationEditorPage />
                    </ThemeProvider>
                  </RequireProfessionalAuth>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/mass-communication/:orgHandle/:id/analytics'
            element={
              <WorkspaceChangeRedirect to='/mass-communication'>
                <Suspense fallback={<EmbedLoading />}>
                  <RequireProfessionalAuth>
                    <ThemeProvider theme={internalLightTheme}>
                      <MassCommunicationAnalyticsPage />
                    </ThemeProvider>
                  </RequireProfessionalAuth>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/form-letters'
            element={<Navigate to='/settings-hub/form-letters' replace />}
          />
          <Route
            path='/licenses-and-permits'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireFeatureFlag flag={FEATURE_FLAGS.LICENSES_AND_PERMITS}>
                  <RequireProfessionalAuth>
                    <ThemeProvider theme={internalLightTheme}>
                      <LicenseTemplatesScreen />
                    </ThemeProvider>
                  </RequireProfessionalAuth>
                </RequireFeatureFlag>
              </Suspense>
            }
          />
          <Route
            path='/licenses-and-permits/new-template'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireFeatureFlag flag={FEATURE_FLAGS.LICENSES_AND_PERMITS}>
                  <RequireProfessionalAuth>
                    <ThemeProvider theme={internalLightTheme}>
                      <LicenseEditorPage />
                    </ThemeProvider>
                  </RequireProfessionalAuth>
                </RequireFeatureFlag>
              </Suspense>
            }
          />
          <Route
            path='/organizations/:organizationHandle/licenses-and-permits/:licenseTemplateCode/edit-template'
            element={
              <WorkspaceChangeRedirect to='/licenses-and-permits'>
                <Suspense fallback={<EmbedLoading />}>
                  <RequireFeatureFlag flag={FEATURE_FLAGS.LICENSES_AND_PERMITS}>
                    <RequireProfessionalAuth>
                      <ThemeProvider theme={internalLightTheme}>
                        <LicenseEditorPage />
                      </ThemeProvider>
                    </RequireProfessionalAuth>
                  </RequireFeatureFlag>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/organizations/:organizationHandle/licenses-and-permits/:licenseTemplateCode'
            element={
              <WorkspaceChangeRedirect to='/licenses-and-permits'>
                <Suspense fallback={<EmbedLoading />}>
                  <RequireFeatureFlag flag={FEATURE_FLAGS.LICENSES_AND_PERMITS}>
                    <RequireProfessionalAuth>
                      <ThemeProvider theme={internalLightTheme}>
                        <LicenseTemplatePage />
                      </ThemeProvider>
                    </RequireProfessionalAuth>
                  </RequireFeatureFlag>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/organizations/:organizationHandle/licenses-and-permits/:licenseTemplateCode/:licenseNumber'
            element={
              <WorkspaceChangeRedirect to='/licenses-and-permits'>
                <Suspense fallback={<EmbedLoading />}>
                  <RequireFeatureFlag flag={FEATURE_FLAGS.LICENSES_AND_PERMITS}>
                    <RequireProfessionalAuth>
                      <ThemeProvider theme={internalLightTheme}>
                        <LicensePage />
                      </ThemeProvider>
                    </RequireProfessionalAuth>
                  </RequireFeatureFlag>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/form-letters/:organizationHandle/:templateCode'
            element={
              <WorkspaceChangeRedirect to='/settings-hub/form-letters'>
                <Suspense fallback={<EmbedLoading />}>
                  <RequireProfessionalAuth>
                    <ThemeProvider theme={internalLightTheme}>
                      <FormLetterPage />
                    </ThemeProvider>
                  </RequireProfessionalAuth>
                </Suspense>
              </WorkspaceChangeRedirect>
            }
          />
          <Route
            path='/super'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <SuperUserDashboard />
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/home'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <RequireFeatureFlag flag={FEATURE_FLAGS.ENABLE_MANAGER_STAFF_VIEW}>
                    <HomepageScreen />
                  </RequireFeatureFlag>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
          <Route
            path='/public-records'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireFeatureFlag flag={FEATURE_FLAGS.PUBLIC_RECORDS_STUDIO}>
                  <RequireProfessionalAuth>
                    <ThemeProvider theme={internalLightTheme}>
                      <PublicRecordsStudioRequestsScreen />
                    </ThemeProvider>
                  </RequireProfessionalAuth>
                </RequireFeatureFlag>
              </Suspense>
            }
          />
        </Route>

        {/* Standalone routes — no sidebar */}
        <Route
          path='/public-records/:redactionRequestId'
          element={
            <WorkspaceChangeRedirect to='/public-records'>
              <Suspense fallback={<EmbedLoading />}>
                <RequireFeatureFlag flag={FEATURE_FLAGS.PUBLIC_RECORDS_STUDIO}>
                  <RequireProfessionalAuth>
                    <ThemeProvider theme={internalLightTheme}>
                      <div className='prs-standalone'>
                        <SessionTimeoutManager />
                        <div className='prs-standalone-body'>
                          <PublicRecordsStudioRedactionScreen />
                        </div>
                      </div>
                    </ThemeProvider>
                  </RequireProfessionalAuth>
                </RequireFeatureFlag>
              </Suspense>
            </WorkspaceChangeRedirect>
          }
        />
        {!IS_PROD && (
          <Route
            path='/design-system-internal'
            element={
              <Suspense fallback={<EmbedLoading />}>
                <RequireProfessionalAuth>
                  <ThemeProvider theme={internalLightTheme}>
                    <DesignSystemInternal />
                  </ThemeProvider>
                </RequireProfessionalAuth>
              </Suspense>
            }
          />
        )}
        <Route path='/health' element={<HealthCheck />} />
        <Route
          path='/reset-password/:userId/:resetCode'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <ThemeProvider theme={internalLightTheme}>
                <ResetPasswordScreen />
              </ThemeProvider>
            </Suspense>
          }
        />
        <Route
          path='/verify/:emailId/:code'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <ThemeProvider theme={internalLightTheme}>
                <VerifyScreen />
              </ThemeProvider>
            </Suspense>
          }
        />
        <Route
          path='/sso/confirm-link'
          element={
            <Suspense fallback={<EmbedLoading />}>
              <ThemeProvider theme={internalLightTheme}>
                <SSOConfirmLinkScreen />
              </ThemeProvider>
            </Suspense>
          }
        />
        <Route path='*' element={<NotFound />} />
      </Routes>
    </Box>
  )
}

const getBasename = (): string => {
  // Support serving the app at both / and /react/
  // If the URL starts with /react, use /react as the basename; otherwise use /
  return window.location.pathname === '/react' || window.location.pathname.startsWith('/react/')
    ? '/react'
    : '/'
}

const AppWrapper = (): ReactElement => {
  return (
    <BrowserRouter basename={getBasename()}>
      <TitleUpdater />
      <WorkspaceRedirectProvider>
        <App />
      </WorkspaceRedirectProvider>
    </BrowserRouter>
  )
}

export default AppWrapper
